

import React, { useState, useEffect } from 'react';
import type { GridColumn, ColumnType, SemanticType } from '../types/gridTypes';
import { IconSettings, IconX, IconPlus } from './icons';
import { FormulaAutocomplete, type AutocompleteItem } from './FormulaAutocomplete';

interface Props {
  column: GridColumn;
  isNew?: boolean;
  onSave: (column: GridColumn) => void;
  onClose: () => void;
  columns?: GridColumn[];
}

// Mapping of Column Types to valid Semantic Types
const SEMANTIC_OPTIONS: Record<string, { value: SemanticType; label: string }[]> = {
  text: [
    { value: 'text', label: 'Plain Text' },
    { value: 'identifier', label: 'ID / Code' },
    { value: 'owner', label: 'Person / Owner' },
    { value: 'status', label: 'Status Label' }
  ],
  number: [
    { value: 'numeric_metric', label: 'General Number' },
    { value: 'money', label: 'Currency Amount' },
    { value: 'percentage', label: 'Percentage' }
  ],
  currency: [
    { value: 'money', label: 'Currency' }
  ],
  percent: [
    { value: 'percentage', label: 'Percentage' }
  ],
  date: [
    { value: 'date', label: 'Date' }
  ],
  select: [
    { value: 'status', label: 'Status / Category' },
    { value: 'text', label: 'Text Options' }
  ],
  'multi-select': [
    { value: 'text', label: 'Tags / Categories' },
    { value: 'owner', label: 'People / Assignees' }
  ],
  rating: [
    { value: 'status', label: 'R/Y/G Status' }
  ],
  checkbox: [
    { value: 'status', label: 'Boolean / Flag' }
  ],
  formula: [
    { value: 'numeric_metric', label: 'Number' },
    { value: 'money', label: 'Currency' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'duration', label: 'Duration (h:m)' },
    { value: 'text', label: 'Text' },
    { value: 'date', label: 'Date' },
    { value: 'status', label: 'Status' }
  ]
};

export const ColumnConfigModal: React.FC<Props> = ({ column, isNew, onSave, onClose, columns = [] }) => {
  const [formData, setFormData] = useState<GridColumn>({ ...column });
  
  // Autocomplete state
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setFormData({ ...column });
  }, [column]);

  const handleChange = (field: keyof GridColumn, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (newType: ColumnType) => {
    // Auto-select the first valid semantic type for this column type
    const availableSemantics = SEMANTIC_OPTIONS[newType] || [];
    const defaultSemantic = availableSemantics.length > 0 ? availableSemantics[0].value : undefined;

    // Set default options for RYG if rating selected and no options present
    let newOptions = formData.options;
    if (newType === 'rating') {
      newOptions = ['Red', 'Yellow', 'Green', 'Gray'];
    }

    setFormData(prev => ({
      ...prev,
      type: newType,
      semanticType: defaultSemantic,
      options: newOptions
    }));
  };

  const handleOptionsChange = (text: string) => {
    const options = text.split(',').map(s => s.trim()).filter(s => s !== '');
    handleChange('options', options);
  };

  const handleFormulaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    handleChange('formulaExpression', val);
    setCursorPosition(pos);
    setShowAutocomplete(true);
  };

  const handleAutocompleteSelect = (item: AutocompleteItem, start: number, end: number) => {
      const val = formData.formulaExpression || '';
      const prefix = val.slice(0, start);
      const suffix = val.slice(end);
      
      let insert = item.value;
      if (item.type === 'column') {
          insert = `[${item.label}]`;
      }
      
      const newVal = prefix + insert + suffix;
      handleChange('formulaExpression', newVal);
      setShowAutocomplete(false);
      
      // Restore focus and set cursor
      setTimeout(() => {
          if (textareaRef.current) {
              textareaRef.current.focus();
              const newPos = prefix.length + insert.length;
              textareaRef.current.setSelectionRange(newPos, newPos);
          }
      }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleStyleChange = (field: string, value: any) => {
     const newStyle = { ...(formData.style || {}), [field]: value };
     handleChange('style', newStyle);
  };

  const validSemanticOptions = SEMANTIC_OPTIONS[formData.type] || [];

  return (
    <div className="modal-overlay fixed inset-0 flex items-center justify-center backdrop-blur-sm">
      <div className="modal-container rounded-lg shadow-2xl w-[500px] max-w-[90vw] flex flex-col max-h-[85vh]">
        <div className="modal-header flex items-center justify-between p-4 border-b">
          <h3 className="modal-title font-bold flex items-center gap-2">
            {isNew ? <IconPlus className="w-5 h-5 modal-icon-success" /> : <IconSettings className="w-5 h-5 modal-icon" />}
            {isNew ? 'New Column' : `Edit Column: ${column.label}`}
          </h3>
          <button onClick={onClose} className="modal-close-btn">
            <IconX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-content flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title */}
          <div className="space-y-1">
            <label className="modal-label block text-sm font-medium">Column Title</label>
            <input
              type="text"
              className="modal-input w-full border rounded px-3 py-2 text-sm outline-none"
              value={formData.label}
              onChange={e => handleChange('label', e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Type & Semantic Type Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="modal-label block text-sm font-medium">Column Type</label>
              <select
                className="modal-select w-full border rounded px-3 py-2 text-sm outline-none"
                value={formData.type}
                onChange={e => handleTypeChange(e.target.value as ColumnType)}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
                <option value="percent">Percent</option>
                <option value="date">Date</option>
                <option value="select">Single Select</option>
                <option value="multi-select">Multi-Select</option>
                <option value="rating">Icon (R/Y/G)</option>
                <option value="checkbox">Checkbox</option>
                <option value="formula">Formula</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="modal-label block text-sm font-medium">
                 Meaning (Semantic)
              </label>
              <select
                className={`modal-select w-full border rounded px-3 py-2 text-sm outline-none ${validSemanticOptions.length <= 1 ? 'modal-select-disabled' : ''}`}
                value={formData.semanticType || ''}
                onChange={e => handleChange('semanticType', e.target.value || undefined)}
                disabled={validSemanticOptions.length <= 1}
              >
                {validSemanticOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Select Options */}
          {(formData.type === 'select' || formData.type === 'multi-select') && (
            <div className="space-y-1">
              <label className="modal-label block text-sm font-medium">
                Options (comma separated)
              </label>
              <textarea
                className="modal-input w-full border rounded px-3 py-2 text-sm outline-none"
                rows={3}
                value={(formData.options || []).join(', ')}
                onChange={e => handleOptionsChange(e.target.value)}
                placeholder="Option 1, Option 2, Option 3"
              />
            </div>
          )}

          {/* Rating Info */}
          {formData.type === 'rating' && (
             <div className="modal-info-box p-3 text-xs rounded border">
                Displays Red, Yellow, Green, or Gray status dots.
             </div>
          )}

          {/* Text Options: Wrap Text */}
          {(formData.type === 'text' || formData.semanticType === 'text' || formData.type === 'multi-select') && (
            <div className="flex items-center gap-2 pt-2">
               <input
                  type="checkbox"
                  id="wrapText"
                  className="modal-checkbox w-4 h-4 rounded"
                  checked={formData.style?.wrapText || false}
                  onChange={e => handleStyleChange('wrapText', e.target.checked)}
               />
               <label htmlFor="wrapText" className="modal-label text-sm cursor-pointer select-none">
                  Wrap text
               </label>
            </div>
          )}

          {/* Formula - Inline-first per UX spec */}
          {formData.type === 'formula' && (
            <div className="space-y-2">
              <label className="modal-label block text-sm font-medium">
                Column Formula
              </label>
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  className="modal-input w-full border rounded px-3 py-2 text-sm font-mono outline-none"
                  rows={3}
                  value={formData.formulaExpression || ''}
                  onChange={handleFormulaChange}
                  onSelect={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                  placeholder="=[Revenue] - [Cost]"
                />
                {showAutocomplete && (
                  <FormulaAutocomplete
                    inputValue={formData.formulaExpression || ''}
                    cursorPosition={cursorPosition}
                    columns={columns.map(c => ({ id: c.id, label: c.label }))}
                    onSelect={handleAutocompleteSelect}
                    onClose={() => setShowAutocomplete(false)}
                    position={{ top: 80, left: 0 }}
                  />
                )}
              </div>
              <div className="modal-info-box p-3 text-xs rounded border space-y-1">
                <p><strong>Syntax:</strong> Use <code className="font-mono bg-slate-200 dark:bg-slate-600 px-1 rounded">[ColumnName]</code> to reference other columns.</p>
                <p><strong>Examples:</strong></p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li><code className="font-mono text-[11px]">=[Price] * [Quantity]</code></li>
                  <li><code className="font-mono text-[11px]">=SUM([Revenue], [OtherIncome])</code></li>
                  <li><code className="font-mono text-[11px]">=IF([Status]="Done", 100, 0)</code></li>
                </ul>
                <p className="text-muted-foreground mt-1">Supports 400+ Excel functions via HyperFormula.</p>
              </div>
            </div>
          )}

          {/* Numeric/Date/Unit Props */}
          <div className="grid grid-cols-2 gap-4">
               {/* Unit Label - Only for actual inputs or numeric formulas */}
               {['number', 'formula'].includes(formData.type) && !['date', 'text', 'status'].includes(formData.semanticType || '') && (
                   <div className="space-y-1">
                      <label className="modal-label block text-sm font-medium">Unit Label</label>
                      <input
                        type="text"
                        className="modal-input w-full border rounded px-3 py-2 text-sm outline-none"
                        value={formData.unit || ''}
                        onChange={e => handleChange('unit', e.target.value)}
                        placeholder="e.g. kg, hrs, $"
                      />
                   </div>
               )}

               {/* Decimals - Only for numeric types */}
               {(['number', 'currency', 'percent'].includes(formData.type) ||
                 (formData.type === 'formula' && ['numeric_metric', 'money', 'percentage'].includes(formData.semanticType || ''))) && (
                   <div className="space-y-1">
                      <label className="modal-label block text-sm font-medium">Decimal Places</label>
                       <select
                          className="modal-select w-full border rounded px-3 py-2 text-sm outline-none"
                          value={formData.numberFormat?.decimals ?? -1}
                          onChange={e => {
                              const val = parseInt(e.target.value);
                              const newFmt = { ...(formData.numberFormat || {}), decimals: val === -1 ? undefined : val };
                              handleChange('numberFormat', newFmt);
                          }}
                      >
                          <option value={-1}>Auto</option>
                          <option value={0}>0</option>
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                          <option value={4}>4</option>
                      </select>
                  </div>
               )}

               {/* Date Format */}
               {(formData.type === 'date' || formData.semanticType === 'date') && (
                  <div className="space-y-1 col-span-2">
                     <label className="modal-label block text-sm font-medium">Date Format</label>
                     <select
                         className="modal-select w-full border rounded px-3 py-2 text-sm outline-none"
                         value={formData.dateFormat || ''}
                         onChange={e => handleChange('dateFormat', e.target.value)}
                     >
                         <option value="">Default (Locale)</option>
                         <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                         <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                         <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                     </select>
                  </div>
               )}
          </div>

          <div className="space-y-1">
             <label className="modal-label block text-sm font-medium">Description</label>
             <input
               type="text"
               className="modal-input w-full border rounded px-3 py-2 text-sm outline-none"
               value={formData.description || ''}
               onChange={e => handleChange('description', e.target.value)}
               placeholder="Optional description for AI context"
             />
          </div>

        </form>

        <div className="modal-footer p-4 border-t flex justify-end gap-2 rounded-b-lg">
          <button
            onClick={onClose}
            className="modal-btn-secondary px-4 py-2 text-sm rounded font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="modal-btn-primary px-4 py-2 text-sm rounded font-medium shadow-sm"
          >
            {isNew ? 'Create Column' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
